import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getLocalizedTime } from "../../utils/timeZone";
import TextWrapper from "../textWrapper/TextWrapper";
import styles from "./timelineTable.module.css";
import TimelineTableHeader from "./timelineTableHeader/TimelineTableHeader";

const TimelineTable = ({
  timelineData,
  tournamentId,
  autoScrollToRight = false,
}) => {
  const { timeZone } = useSelector((state) => state.timeZone);
  const containerRef = useRef(null);
  const uniqueDates = [...new Set(timelineData.map((item) => item.slotStart))];

  const groupedTimelines = timelineData.reduce((acc, timeline) => {
    if (!acc[timeline.setNumber]) {
      acc[timeline.setNumber] = {};
    }
    acc[timeline.setNumber][timeline.slotStart] = timeline;
    return acc;
  }, {});

  const setNumbers = Object.keys(groupedTimelines);

  useEffect(() => {
    if (!autoScrollToRight) return;

    const container = containerRef.current;
    if (!container) return;

    const scrollToRight = () => {
      container.scrollLeft = Math.max(
        0,
        container.scrollWidth - container.clientWidth,
      );
    };

    const frameId = requestAnimationFrame(scrollToRight);
    return () => cancelAnimationFrame(frameId);
  }, [autoScrollToRight, setNumbers.length, uniqueDates.length]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.tableAnchor}>
        <table className={styles.timelineTable}>
          <thead>
            <tr>
              <td
                className={`${styles.timelineCell} ${styles.rowNumberCell} ${styles.rowNumberHeaderCell}`}
              >
                <TextWrapper textStyle={"textMid"}>رقم السطر</TextWrapper>
              </td>
              <td className={`${styles.timelineCell} ${styles.dateCell}`}>
                <TextWrapper textStyle={"textMid"}>المواعيد</TextWrapper>
              </td>
              {setNumbers.map((setNumber) => (
                <TimelineTableHeader
                  key={setNumber}
                  setNumber={setNumber}
                  tournamentId={tournamentId}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {uniqueDates.map((date, index) => (
              <tr key={date} className={styles.tableRow}>
                <td className={`${styles.timelineCell} ${styles.rowNumberCell}`}>
                  <TextWrapper textStyle={"textSmall"}>{index + 1}</TextWrapper>
                </td>
                <td className={`${styles.timelineCell} ${styles.dateCell}`}>
                  <TextWrapper>
                    {
                      getLocalizedTime(date, timeZone)
                        .toLocaleString()
                        .split(",")[0]
                    }
                  </TextWrapper>
                  <TextWrapper>
                    {
                      getLocalizedTime(date, timeZone)
                        .toLocaleString()
                        .split(",")[1]
                    }
                  </TextWrapper>
                </td>
                {setNumbers.map((setNumber) => {
                  const timeline = groupedTimelines[setNumber][date];

                  return (
                    <td
                      key={`${setNumber}-${date}`}
                      className={`${styles.timelineCell} ${styles.roomInfo}`}
                    >
                      {timeline?.tableId ? (
                        <div className={styles.roomCard}>
                          <div className={styles.roomMeta}>
                            <span className={styles.roomLabel}>الاسم</span>
                            <p className={styles.roomValue}>
                              {timeline.tableId.name}
                            </p>
                          </div>

                          <div className={styles.roomMetaGrid}>
                            <div className={styles.roomMetaTile}>
                              <span className={styles.roomLabel}>المنطقة</span>
                              <p className={styles.roomValue}>
                                {timeline.tableId.region}
                              </p>
                            </div>
                            <div className={styles.roomMetaTile}>
                              <span className={styles.roomLabel}>
                                عدد اللاعبين
                              </span>
                              <p className={styles.roomValue}>
                                {timeline.tableId.playersLimit}
                              </p>
                            </div>
                          </div>

                          <div className={styles.roomStatusRow}>
                            <span className={styles.roomLabel}>الحالة</span>
                            <span
                              className={`${styles.roomStatus} ${
                                timeline.tableId.isFull
                                  ? styles.roomStatusFull
                                  : styles.roomStatusOpen
                              }`}
                            >
                              {timeline.tableId.isFull ? "ممتلئ" : "متاح"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.emptyRoomCell}>لا يوجد جدول</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimelineTable;
